// src/pages/SignupPage.js
// Page d'inscription CoryntiQ - Onboarding complet avec Moneris
// Flow: Compte → Organisation → Plan → Paiement → Bienvenue

import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { getActiveConfig } from "../config/moneris";
import MonerisPaymentForm from "../components/payment/MonerisPaymentForm";
import {
  Mail,
  Lock,
  User,
  Building2,
  Users,
  CreditCard,
  Check,
  ChevronRight,
  ChevronLeft,
  Eye,
  EyeOff,
  Shield,
  Loader2,
  AlertCircle,
  Sparkles,
  Clock,
  Zap,
  Crown,
  Phone,
} from "lucide-react";

// ============================================
// CONFIGURATION DES PLANS
// ============================================

const PLANS = {
  essentiel: {
    id: "essentiel",
    name: "Essentiel",
    priceMonthly: 199,
    priceAnnual: 1990,
    description: "Idéal pour les PME qui débutent leur conformité",
    icon: Zap,
    color: "sky",
    features: [
      "Tous les modules inclus",
      "Jusqu'à 3 utilisateurs",
      "Rapports standards",
      "Support par courriel (48h)",
    ],
    popular: false,
  },
  professionnel: {
    id: "professionnel",
    name: "Professionnel",
    priceMonthly: 449,
    priceAnnual: 4490,
    description: "Pour les entreprises qui veulent aller plus loin",
    icon: Crown,
    color: "indigo",
    features: [
      "Tout dans Essentiel",
      "Utilisateurs illimités",
      "Rapports personnalisés",
      "Intégration API",
      "Support prioritaire (4h)",
      "1h d'onboarding incluse",
      'Badge "Conforme Loi 25"',
    ],
    popular: true,
  },
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const SignupPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const config = getActiveConfig() || {};

  // État du formulaire multi-étapes
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Déterminer le plan initial depuis l'URL
  const initialPlan = searchParams.get("plan");
  const defaultPlanId =
    initialPlan && PLANS[initialPlan] ? initialPlan : "essentiel";

  // Données du formulaire
  const [formData, setFormData] = useState({
    // Étape 1: Compte
    email: "",
    password: "",
    confirmPassword: "",
    prenom: "",
    nom: "",

    // Étape 2: Organisation
    organisationNom: "",
    secteur: "",
    nombreEmployes: "",
    telephone: "",

    // Étape 3: Plan
    planId: defaultPlanId,
    billingFrequency: "monthly",

    // Étape 4: Paiement
    userId: null,
    organisationId: null,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);

  // Token Moneris
  const [monerisToken, setMonerisToken] = useState(null);
  const [cardInfo, setCardInfo] = useState(null);

  // ============================================
  // ÉTAPES DU FORMULAIRE
  // ============================================

  const steps = [
    { id: 1, name: "Compte", icon: User },
    { id: 2, name: "Organisation", icon: Building2 },
    { id: 3, name: "Plan", icon: Sparkles },
    { id: 4, name: "Paiement", icon: CreditCard },
  ];

  // ============================================
  // HANDLERS
  // ============================================

  const updateFormData = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (
          !formData.email ||
          !formData.password ||
          !formData.prenom ||
          !formData.nom
        ) {
          setError("Veuillez remplir tous les champs");
          return false;
        }
        if (formData.password.length < 8) {
          setError("Le mot de passe doit contenir au moins 8 caractères");
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          setError("Les mots de passe ne correspondent pas");
          return false;
        }
        if (!acceptTerms) {
          setError("Veuillez accepter les conditions d'utilisation");
          return false;
        }
        return true;
      case 2:
        if (!formData.organisationNom || !formData.secteur) {
          setError("Veuillez remplir les champs obligatoires");
          return false;
        }
        return true;
      case 3:
        return true;
      case 4:
        if (!monerisToken) {
          setError("Veuillez valider votre carte de paiement");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  // ============================================
  // ÉTAPE 1: CRÉER LE COMPTE
  // ============================================

  const handleCreateAccount = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            prenom: formData.prenom,
            nom: formData.nom,
          },
        },
      });

      if (authError) throw authError;

      updateFormData("userId", authData.user.id);
      setCurrentStep(2);
    } catch (err) {
      if (err.message.includes("already registered")) {
        setError("Cet email est déjà utilisé. Essayez de vous connecter.");
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // ÉTAPE 2: CRÉER L'ORGANISATION
  // ============================================

  const handleCreateOrganisation = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data: orgData, error: orgError } = await supabase
        .from("organisations")
        .insert({
          nom: formData.organisationNom,
          secteur: formData.secteur,
          nombre_employes: formData.nombreEmployes || null,
          telephone: formData.telephone || null,
          plan_actif: "essai",
          created_by: formData.userId,
        })
        .select()
        .single();

      if (orgError) throw orgError;

      await supabase.from("profils").upsert({
        id: formData.userId,
        organisation_id: orgData.id,
        prenom: formData.prenom,
        nom: formData.nom,
        email: formData.email,
        role: "admin",
      });

      updateFormData("organisationId", orgData.id);
      setCurrentStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // CALLBACK MONERIS TOKEN
  // ============================================

  const handleTokenReceived = (tokenData) => {
    console.log("Token received:", tokenData);
    setMonerisToken(tokenData.dataKey);
    setCardInfo({
      lastFour: tokenData.lastFour,
      cardType: tokenData.cardType,
    });
    setError(null);
  };

  const handleTokenError = (errorMsg) => {
    setError(errorMsg);
    setMonerisToken(null);
  };

  // ============================================
  // ÉTAPE 4: CRÉER L'ABONNEMENT
  // ============================================

  const handleCreateSubscription = async () => {
    if (!monerisToken) {
      setError("Veuillez d'abord valider votre carte");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const plan = PLANS[formData.planId];
      const amount =
        formData.billingFrequency === "annual"
          ? plan.priceAnnual
          : plan.priceMonthly;

      // Appeler l'Edge Function Supabase
      const { data, error: funcError } = await supabase.functions.invoke(
        "create-subscription",
        {
          body: {
            dataKey: monerisToken,
            planId: formData.planId,
            billingFrequency: formData.billingFrequency,
            customer: {
              email: formData.email,
              name: `${formData.prenom} ${formData.nom}`,
              userId: formData.userId,
            },
            organisationId: formData.organisationId,
          },
        }
      );

      if (funcError) throw funcError;

      if (data.error) {
        throw new Error(data.error);
      }

      // Succès! Rediriger vers le dashboard
      navigate("/dashboard?welcome=true&plan=" + formData.planId);
    } catch (err) {
      console.error("Subscription error:", err);
      setError(err.message || "Erreur lors de la création de l'abonnement");
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    if (!validateStep(currentStep)) return;

    switch (currentStep) {
      case 1:
        await handleCreateAccount();
        break;
      case 2:
        await handleCreateOrganisation();
        break;
      case 3:
        setCurrentStep(4);
        break;
      case 4:
        await handleCreateSubscription();
        break;
      default:
        break;
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError(null);
    }
  };

  // ============================================
  // CALCUL DU PRIX
  // ============================================

  const selectedPlan = PLANS[formData.planId] || PLANS.essentiel;
  const price =
    formData.billingFrequency === "annual"
      ? selectedPlan.priceAnnual
      : selectedPlan.priceMonthly;
  const savings =
    formData.billingFrequency === "annual"
      ? selectedPlan.priceMonthly * 12 - selectedPlan.priceAnnual
      : 0;

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex">
      {/* Colonne gauche - Branding */}
      <div className="hidden lg:flex lg:w-2/5 bg-gradient-to-br from-sky-600 to-sky-700 p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 border border-white rounded-full"></div>
          <div className="absolute bottom-40 right-0 w-96 h-96 border border-white rounded-full -mr-48"></div>
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-16">
            <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <span className="text-2xl font-bold text-white">CoryntiQ</span>
          </div>

          <h1 className="text-4xl font-bold text-white mb-6 leading-tight">
            Simplifiez votre conformité à la Loi 25
          </h1>
          <p className="text-xl text-sky-100 mb-12">
            Rejoignez les PME québécoises qui font confiance à CoryntiQ.
          </p>

          <div className="space-y-4">
            {[
              "Essai gratuit de 14 jours",
              "Aucune carte débitée avant la fin de l'essai",
              "Annulation facile en tout temps",
              "Données hébergées au Canada 🇨🇦",
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3 text-white">
                <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4" />
                </div>
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 bg-white/10 backdrop-blur rounded-2xl p-6">
          <p className="text-white italic mb-4">
            "CoryntiQ nous a permis de mettre en place notre conformité Loi 25
            en quelques jours seulement."
          </p>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-white font-semibold">Marie-Claude T.</p>
              <p className="text-sky-200 text-sm">DG, Clinique Santé Plus</p>
            </div>
          </div>
        </div>
      </div>

      {/* Colonne droite - Formulaire */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-lg">
          {/* Progress bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              {steps.map((step, index) => {
                const StepIcon = step.icon;
                return (
                  <React.Fragment key={step.id}>
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                          currentStep > step.id
                            ? "bg-green-500 text-white"
                            : currentStep === step.id
                            ? "bg-sky-500 text-white"
                            : "bg-slate-700 text-slate-400"
                        }`}
                      >
                        {currentStep > step.id ? (
                          <Check className="w-5 h-5" />
                        ) : (
                          <StepIcon className="w-5 h-5" />
                        )}
                      </div>
                      <span
                        className={`text-xs mt-2 ${
                          currentStep >= step.id
                            ? "text-white"
                            : "text-slate-500"
                        }`}
                      >
                        {step.name}
                      </span>
                    </div>
                    {index < steps.length - 1 && (
                      <div
                        className={`flex-1 h-0.5 mx-2 ${
                          currentStep > step.id
                            ? "bg-green-500"
                            : "bg-slate-700"
                        }`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Card du formulaire */}
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-2xl p-8">
            {/* Erreur */}
            {error && (
              <div className="mb-6 flex items-center gap-2 bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-xl">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            {/* ÉTAPE 1: COMPTE */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">
                    Créez votre compte
                  </h2>
                  <p className="text-slate-400">
                    Commencez votre essai gratuit de 14 jours
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Prénom
                    </label>
                    <input
                      type="text"
                      value={formData.prenom}
                      onChange={(e) => updateFormData("prenom", e.target.value)}
                      className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                      placeholder="Jean"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Nom
                    </label>
                    <input
                      type="text"
                      value={formData.nom}
                      onChange={(e) => updateFormData("nom", e.target.value)}
                      className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                      placeholder="Tremblay"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Courriel professionnel
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateFormData("email", e.target.value)}
                      className="w-full pl-12 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                      placeholder="jean@entreprise.ca"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Mot de passe
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) =>
                        updateFormData("password", e.target.value)
                      }
                      className="w-full pl-12 pr-12 py-3 bg-slate-900/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                      placeholder="Minimum 8 caractères"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Confirmer le mot de passe
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={(e) =>
                        updateFormData("confirmPassword", e.target.value)
                      }
                      className="w-full pl-12 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                      placeholder="Confirmer le mot de passe"
                    />
                  </div>
                </div>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-slate-600 bg-slate-900 text-sky-500 focus:ring-sky-500"
                  />
                  <span className="text-sm text-slate-400">
                    J'accepte les{" "}
                    <a href="/terms" className="text-sky-400 hover:underline">
                      conditions d'utilisation
                    </a>{" "}
                    et la{" "}
                    <a href="/privacy" className="text-sky-400 hover:underline">
                      politique de confidentialité
                    </a>
                  </span>
                </label>
              </div>
            )}

            {/* ÉTAPE 2: ORGANISATION */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">
                    Votre organisation
                  </h2>
                  <p className="text-slate-400">
                    Quelques informations pour personnaliser votre expérience
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Nom de l'entreprise *
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <input
                      type="text"
                      value={formData.organisationNom}
                      onChange={(e) =>
                        updateFormData("organisationNom", e.target.value)
                      }
                      className="w-full pl-12 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                      placeholder="Entreprise ABC Inc."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Secteur d'activité *
                  </label>
                  <select
                    value={formData.secteur}
                    onChange={(e) => updateFormData("secteur", e.target.value)}
                    className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-xl text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  >
                    <option value="">Sélectionnez un secteur</option>
                    <option value="sante">Santé</option>
                    <option value="finance">Finance et assurances</option>
                    <option value="commerce">Commerce de détail</option>
                    <option value="services">Services professionnels</option>
                    <option value="technologie">Technologie</option>
                    <option value="manufacturier">Manufacturier</option>
                    <option value="education">Éducation</option>
                    <option value="immobilier">Immobilier</option>
                    <option value="transport">Transport et logistique</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Nombre d'employés
                  </label>
                  <div className="relative">
                    <Users className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <select
                      value={formData.nombreEmployes}
                      onChange={(e) =>
                        updateFormData("nombreEmployes", e.target.value)
                      }
                      className="w-full pl-12 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-xl text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                    >
                      <option value="">Sélectionnez</option>
                      <option value="1-10">1-10 employés</option>
                      <option value="11-50">11-50 employés</option>
                      <option value="51-100">51-100 employés</option>
                      <option value="101-250">101-250 employés</option>
                      <option value="250+">250+ employés</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Téléphone (optionnel)
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <input
                      type="tel"
                      value={formData.telephone}
                      onChange={(e) =>
                        updateFormData("telephone", e.target.value)
                      }
                      className="w-full pl-12 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                      placeholder="(514) 555-1234"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ÉTAPE 3: CHOIX DU PLAN */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">
                    Choisissez votre plan
                  </h2>
                  <p className="text-slate-400">
                    14 jours d'essai gratuit, sans engagement
                  </p>
                </div>

                {/* Toggle Mensuel/Annuel */}
                <div className="flex items-center justify-center gap-4 p-1 bg-slate-900/50 rounded-xl">
                  <button
                    type="button"
                    onClick={() =>
                      updateFormData("billingFrequency", "monthly")
                    }
                    className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                      formData.billingFrequency === "monthly"
                        ? "bg-sky-500 text-white"
                        : "text-slate-400 hover:text-white"
                    }`}
                  >
                    Mensuel
                  </button>
                  <button
                    type="button"
                    onClick={() => updateFormData("billingFrequency", "annual")}
                    className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                      formData.billingFrequency === "annual"
                        ? "bg-sky-500 text-white"
                        : "text-slate-400 hover:text-white"
                    }`}
                  >
                    Annuel
                    <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                      -17%
                    </span>
                  </button>
                </div>

                {/* Cards des plans */}
                <div className="space-y-4">
                  {Object.values(PLANS).map((plan) => {
                    const PlanIcon = plan.icon;
                    const planPrice =
                      formData.billingFrequency === "annual"
                        ? plan.priceAnnual
                        : plan.priceMonthly;
                    const isSelected = formData.planId === plan.id;

                    return (
                      <div
                        key={plan.id}
                        onClick={() => updateFormData("planId", plan.id)}
                        className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all ${
                          isSelected
                            ? "border-sky-500 bg-sky-500/10"
                            : "border-slate-700 bg-slate-900/30 hover:border-slate-600"
                        }`}
                      >
                        {plan.popular && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-sky-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                            Populaire
                          </div>
                        )}

                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                isSelected ? "bg-sky-500" : "bg-slate-700"
                              }`}
                            >
                              <PlanIcon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-white">
                                {plan.name}
                              </h3>
                              <p className="text-sm text-slate-400">
                                {plan.description}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-white">
                              {planPrice}$
                              <span className="text-sm font-normal text-slate-400">
                                /
                                {formData.billingFrequency === "annual"
                                  ? "an"
                                  : "mois"}
                              </span>
                            </p>
                          </div>
                        </div>

                        <div className="mt-4 grid grid-cols-2 gap-2">
                          {plan.features.slice(0, 4).map((feature, i) => (
                            <div
                              key={i}
                              className="flex items-center gap-2 text-sm text-slate-300"
                            >
                              <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                              <span>{feature}</span>
                            </div>
                          ))}
                        </div>

                        <div
                          className={`absolute top-6 right-6 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            isSelected
                              ? "border-sky-500 bg-sky-500"
                              : "border-slate-600"
                          }`}
                        >
                          {isSelected && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Récap */}
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700">
                  <div className="flex items-center gap-2 text-green-400 mb-2">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      14 jours d'essai gratuit
                    </span>
                  </div>
                  <p className="text-sm text-slate-400">
                    Votre carte sera débitée de{" "}
                    <span className="text-white font-semibold">{price}$</span>{" "}
                    le{" "}
                    <span className="text-white">
                      {new Date(
                        Date.now() + 14 * 24 * 60 * 60 * 1000
                      ).toLocaleDateString("fr-CA", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </span>{" "}
                    seulement si vous conservez votre abonnement.
                  </p>
                </div>
              </div>
            )}

            {/* ÉTAPE 4: PAIEMENT */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">
                    Informations de paiement
                  </h2>
                  <p className="text-slate-400">
                    Votre carte ne sera débitée qu'après l'essai gratuit
                  </p>
                </div>

                {/* Récap du plan */}
                <div className="bg-sky-500/10 border border-sky-500/30 rounded-xl p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-white font-semibold">
                        Plan {selectedPlan.name}
                      </p>
                      <p className="text-sm text-slate-400">
                        {formData.billingFrequency === "annual"
                          ? "Facturation annuelle"
                          : "Facturation mensuelle"}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-white">{price}$</p>
                      {savings > 0 && (
                        <p className="text-sm text-green-400">
                          Économie de {savings}$/an
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Carte validée */}
                {monerisToken && cardInfo && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center gap-3">
                    <Check className="w-6 h-6 text-green-400" />
                    <div>
                      <p className="text-green-400 font-medium">
                        Carte validée
                      </p>
                      <p className="text-sm text-slate-400">
                        {cardInfo.cardType?.toUpperCase()} se terminant par{" "}
                        {cardInfo.lastFour}
                      </p>
                    </div>
                  </div>
                )}

                {/* Formulaire Moneris */}
                {!monerisToken && (
                  <MonerisPaymentForm
                    onTokenReceived={handleTokenReceived}
                    onError={handleTokenError}
                    amount={price}
                    planName={selectedPlan.name}
                    disabled={loading}
                  />
                )}

                {/* Rappel essai gratuit */}
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-start gap-3">
                  <Clock className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-green-400 font-medium">
                      Aucun frais aujourd'hui
                    </p>
                    <p className="text-sm text-slate-400">
                      Votre essai gratuit de 14 jours commence maintenant.
                      Annulez à tout moment.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* BOUTONS NAVIGATION */}
            <div className="flex gap-4 mt-8">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-3 border border-slate-600 text-slate-300 rounded-xl hover:bg-slate-700 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Retour
                </button>
              )}

              <button
                type="button"
                onClick={handleNext}
                disabled={loading || (currentStep === 4 && !monerisToken)}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-6 rounded-xl font-semibold transition-all ${
                  loading || (currentStep === 4 && !monerisToken)
                    ? "bg-slate-600 text-slate-400 cursor-not-allowed"
                    : "bg-sky-500 text-white hover:bg-sky-600"
                }`}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Traitement...
                  </>
                ) : currentStep === 4 ? (
                  <>
                    Commencer l'essai gratuit
                    <Sparkles className="w-5 h-5" />
                  </>
                ) : (
                  <>
                    Continuer
                    <ChevronRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>

            {currentStep === 1 && (
              <p className="text-center text-slate-400 text-sm mt-6">
                Vous avez déjà un compte?{" "}
                <a href="/login" className="text-sky-400 hover:underline">
                  Se connecter
                </a>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
